import { CButtonClassname, EButtonColor, LoadingSpinner, Section } from '@web_opxp/components';
import { ConfigSelectors, LanguageSelectors, useTranslation } from '@web_opxp/features';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Action, Dispatch } from 'redux';
import { AppSelectors } from '../app';
import ENV from '../base/env';
import VideoPlayer, { ICaptionsConfig } from '../components/VideoPlayer/VideoPlayer';
import { CyclesSelectors } from '../cycles';
import { ExerciseBaseRoute } from '../exercise/Exercise';
import { SessionSelectors } from '../session';
import { IState } from '../store/state';
import FeedbackPopup from './FeedbackPopup';
import './WelcomeTourPage.scss';
import { backToTrainingClickedAction } from './firebaseActions';

interface IWelcomeTourPageProps {
  language?: string;
  loading: boolean;
  exerciseLink: string;
  sessionRunning: boolean;
}

export const WelcomeTourPageFC: React.FC<IWelcomeTourPageProps> = ({
  loading,
  language,
  exerciseLink,
  sessionRunning,
}) => {
  const { t } = useTranslation();

  const dispatch = useDispatch<Dispatch<Action<string>>>();
  const [feedback, setFeedback] = useState<boolean>(false);

  // Add has seen lp to local storage if not already
  useEffect(() => {
    const localStorageWelcomeLp = !!localStorage.getItem('welcome_lp');

    if (localStorageWelcomeLp) {
      return;
    }

    localStorage.setItem('welcome_lp', 'true');
  }, []);

  if (loading) {
    return <LoadingSpinner assetsUrl={ENV.AssetsBaseUrl} />;
  }

  const videoSrc = `${ENV.AssetsBaseUrl}/video/tutorial/welcome_tour.mp4`;
  const deCaptionsSrc = `${ENV.AssetsBaseUrl}/video/tutorial/welcome_tour_de.vtt`;
  const captions: ICaptionsConfig[] = [{ label: t('common:SubtitlesLabel.German'), lang: 'de', src: deCaptionsSrc }];
  const title = t('Welcome.Tour.Page.Title');
  const subtitle = t('Welcome.Tour.Page.Subtitle');
  const item1 = t('Welcome.Tour.Page.Step1');
  const item2 = t('Welcome.Tour.Page.Step2');
  const item3 = t('Welcome.Tour.Page.Step3');
  const ctaLabel = sessionRunning ? t('Welcome.Tour.Page.CTA') : t('Welcome.Tour.Page.Dashboard');
  const linkTo = sessionRunning ? `${ExerciseBaseRoute}/${exerciseLink}` : '/';
  const completeHandler = () => setFeedback(true);
  // track click on firebase
  const backToTrainingHandler = () => dispatch(backToTrainingClickedAction());

  return (
    <Section className="welcome-tour-section">
      <div className="row">
        <div className="col-6 col-m-4 content">
          <h1 className="welcome-tour-title">{title}</h1>
          <h2 className="welcome-tour-subtitle">{subtitle}</h2>
          <ul>
            <li>
              <span>{item1}</span>
            </li>
            <li>
              <span>{item2}</span>
            </li>
            <li>
              <span>{item3}</span>
            </li>
          </ul>
          <Link className={`${CButtonClassname} ${EButtonColor.blue} cta`} to={linkTo} onClick={backToTrainingHandler}>
            {ctaLabel}
          </Link>
        </div>
        <div className="col-6 col-m-0 illustration">
          <div className="wrapper">
            <VideoPlayer
              captions={captions}
              videoSrc={videoSrc}
              language={language}
              onVideoCompletedHandler={completeHandler}
            />
            <FeedbackPopup show={feedback} />
          </div>
        </div>
      </div>
    </Section>
  );
};

export const mapStateToProps = (state: IState) => {
  const appLoading = AppSelectors.isAppLoading(state);
  const localeLoading = LanguageSelectors.isLoading(state);
  const language = LanguageSelectors.getLanguage(state);
  const workoutId = SessionSelectors.getActiveWorkoutId(state);
  const exerciseId = ConfigSelectors.getWorkoutExerciseId(state, workoutId);
  const exerciseLink = exerciseId.toLowerCase();
  const sessionRunning = CyclesSelectors.isSessionRunningStatus(state);

  return {
    language,
    loading: appLoading || localeLoading,
    exerciseLink,
    sessionRunning,
  };
};

const WelcomeTourPage = connect(mapStateToProps)(WelcomeTourPageFC);
export default WelcomeTourPage;
